import type { EffectDefinition, EffectTimelineClip, VideoEffect } from './types';
import { globalEffectRegistry } from './effectRegistry';
import { ColorGradeEffect } from './effects/colorGrade';
import { GlowEffect } from './effects/glow';
import { MotionBlurEffect } from './effects/motionBlur';
import { LensFlareEffect } from './effects/lensFlare';
import { GlitchEffect } from './effects/glitch';
import { HeatDistortionEffect } from './effects/heatDistortion';
import { VhsEffect } from './effects/vhs';
import { ENVIRONMENTAL_EFFECT_DEFINITIONS } from './registryEnvironmental';
import { CINEMATIC_EFFECT_DEFINITIONS } from './registryCinematic';
import { normalizeEffectType } from './effectRegistry';

function register(def: EffectDefinition): void {
  globalEffectRegistry.registerDefinition(def);
}

register({
  type: 'color_grade',
  label: 'Cinematic Color Grade',
  category: 'color',
  defaultParameters: {
    contrast: 1.2, saturation: 1.1, temperature: 0.15, shadows: 0, highlights: 0, exposure: 0,
  },
  parameterSchema: [
    { key: 'contrast', label: 'Contrast', min: 0.5, max: 2, step: 0.05 },
    { key: 'saturation', label: 'Saturation', min: 0, max: 2, step: 0.05 },
    { key: 'temperature', label: 'Temperature', min: -1, max: 1, step: 0.05 },
    { key: 'shadows', label: 'Shadows', min: -1, max: 1, step: 0.05 },
    { key: 'highlights', label: 'Highlights', min: -1, max: 1, step: 0.05 },
    { key: 'exposure', label: 'Exposure', min: -2, max: 2, step: 0.1 },
  ],
  create: (id, clip) => new ColorGradeEffect(id, clip),
});

register({
  type: 'glow',
  label: 'Glow / Bloom',
  category: 'light',
  defaultParameters: { intensity: 0.65, threshold: 0.62, radius: 12 },
  parameterSchema: [
    { key: 'intensity', label: 'Intensity', min: 0, max: 2, step: 0.05 },
    { key: 'threshold', label: 'Threshold', min: 0, max: 1, step: 0.05 },
    { key: 'radius', label: 'Radius', min: 1, max: 24, step: 1 },
  ],
  create: (id, clip) => new GlowEffect(id, clip),
});

register({
  type: 'motion_blur',
  label: 'Motion Blur',
  category: 'motion',
  defaultParameters: { strength: 0.5, sampleCount: 8 },
  parameterSchema: [
    { key: 'strength', label: 'Strength', min: 0, max: 2, step: 0.05 },
    { key: 'sampleCount', label: 'Samples', min: 2, max: 16, step: 1 },
  ],
  create: (id, clip) => new MotionBlurEffect(id, clip),
});

register({
  type: 'lens_flare',
  label: 'Lens Flare',
  category: 'light',
  defaultParameters: { intensity: 0.75, scale: 1.15, positionX: 0.72, positionY: 0.28, streakLength: 1.4 },
  parameterSchema: [
    { key: 'intensity', label: 'Intensity', min: 0, max: 2, step: 0.05 },
    { key: 'scale', label: 'Scale', min: 0.2, max: 3, step: 0.1 },
    { key: 'positionX', label: 'Position X', min: 0, max: 1, step: 0.01 },
    { key: 'positionY', label: 'Position Y', min: 0, max: 1, step: 0.01 },
    { key: 'streakLength', label: 'Streak Length', min: 0, max: 3, step: 0.1 },
  ],
  create: (id, clip) => new LensFlareEffect(id, clip),
});

register({
  type: 'glitch',
  label: 'Glitch',
  category: 'stylize',
  defaultParameters: { intensity: 0.5, rgbOffset: 4, noiseAmount: 0.15, scanLineOpacity: 0.25 },
  parameterSchema: [
    { key: 'intensity', label: 'Intensity', min: 0, max: 1, step: 0.05 },
    { key: 'rgbOffset', label: 'RGB Offset', min: 0, max: 20, step: 1 },
    { key: 'noiseAmount', label: 'Noise', min: 0, max: 1, step: 0.05 },
    { key: 'scanLineOpacity', label: 'Scan Lines', min: 0, max: 1, step: 0.05 },
  ],
  create: (id, clip) => new GlitchEffect(id, clip),
});

register({
  type: 'heat_distortion',
  label: 'Heat Distortion',
  category: 'distortion',
  defaultParameters: { strength: 0.6, speed: 0.4 },
  parameterSchema: [
    { key: 'strength', label: 'Strength', min: 0, max: 2, step: 0.05 },
    { key: 'speed', label: 'Speed', min: 0, max: 2, step: 1 },
  ],
  create: (id, clip) => new HeatDistortionEffect(id, clip),
});

register({
  type: 'vhs',
  label: 'VHS',
  category: 'stylize',
  defaultParameters: {
    intensity: 0.6, rgbOffset: 3, noiseAmount: 0.2, scanLineOpacity: 0.35, blur: 0.4,
  },
  parameterSchema: [
    { key: 'intensity', label: 'Intensity', min: 0, max: 1, step: 0.05 },
    { key: 'rgbOffset', label: 'RGB Offset', min: 0, max: 12, step: 1 },
    { key: 'noiseAmount', label: 'Noise', min: 0, max: 1, step: 0.05 },
    { key: 'scanLineOpacity', label: 'Scan Lines', min: 0, max: 1, step: 0.05 },
    { key: 'blur', label: 'Blur', min: 0, max: 1, step: 0.05 },
  ],
  create: (id, clip) => new VhsEffect(id, clip),
});

for (const def of ENVIRONMENTAL_EFFECT_DEFINITIONS) {
  register(def);
}

for (const def of CINEMATIC_EFFECT_DEFINITIONS) {
  register(def);
}

/** Canonical stack order (background → finishing). */
export const FULL_EFFECT_ORDER = [
  'color_grade',
  'dream_haze',
  'fog',
  'cinematic_clouds',
  'sun_rays',
  'rain',
  'snow',
  'lens_drops',
  'heat_distortion',
  'dust_particles',
  'fire_embers',
  'sparkles',
  'light_leaks',
  'anamorphic_streak',
  'glow',
  'lens_flare',
  'chromatic_aberration',
  'motion_blur',
  'lightning',
  'glitch',
  'vhs',
  'film_grain',
  'vignette',
] as const;

export const DEFAULT_EFFECT_ORDER = FULL_EFFECT_ORDER;

export function getEffectDefinition(type: string): EffectDefinition | undefined {
  return globalEffectRegistry.getDefinition(normalizeEffectType(type));
}

export function listEffectDefinitions(): EffectDefinition[] {
  return globalEffectRegistry.listDefinitions();
}

export function listEffectDefinitionsByCategory(): Record<string, EffectDefinition[]> {
  const grouped: Record<string, EffectDefinition[]> = {};
  for (const def of globalEffectRegistry.listDefinitions()) {
    const cat = def.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(def);
  }
  return grouped;
}

export function createEffectFromClip(clip: EffectTimelineClip): VideoEffect | null {
  const type = normalizeEffectType(clip.type);
  const def = globalEffectRegistry.getDefinition(type);
  if (!def) return null;
  const effect = def.create(clip.id, { ...clip, type });
  globalEffectRegistry.registerInstance(effect);
  return effect;
}

export function createDefaultEffectClip(type: string, startTime: number, endTime: number): EffectTimelineClip | null {
  const normalized = normalizeEffectType(type);
  const def = globalEffectRegistry.getDefinition(normalized);
  if (!def) return null;
  const needsSeed = [
    'glitch', 'vhs', 'rain', 'snow', 'fire_embers', 'dust_particles',
    'lightning', 'lens_drops', 'sparkles', 'film_grain',
  ].includes(normalized);
  return {
    id: `vfx-${normalized}-${Date.now()}`,
    type: normalized,
    name: def.label,
    startTime,
    endTime,
    enabled: true,
    parameters: { ...def.defaultParameters },
    seed: needsSeed ? 42 : undefined,
  };
}

export { globalEffectRegistry as effectRegistry };
